import React, { useState, useEffect } from 'react';
import {
    Typography,
    TextField,
    Button,
    Divider,
    CardContent,
    IconButton,
    Box,
    CardHeader,
    Card,
    Grid
} from '@mui/material';
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Download } from "@mui/icons-material";
import Select from "react-select";
import styles from './Styles';
import { post } from "../../services";
import Cookies from "js-cookie";
import SSPCommonModal from '../../CommonComponents/SSPCommonModal/SSPCommonModal';
import { SSPCommonButton } from '../../CommonComponents';

const FirstTab = ({ rows, rowFetch }) => {
    const [quantity, setQuantity] = useState('');
    const [category, setCategory] = useState(null);
    const [location, setLocation] = useState(null);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [modalOpener, setmodalOpener] = useState(false);
    const [message, setmessage] = useState("");
    const [loading, setLoading] = useState(false);

    // Prod Cookie ID and Base URL
    const id = Cookies.get("ssp_domain_id");
    const base = "https://bjkhbsrllhuogrxobvufau4rxy0qvvxu.lambda-url.ap-south-1.on.aws/";

    useEffect(() => {
        fetchDropdownData();
    }, []);

    const fetchDropdownData = async () => {
        setLoading(true);
        try {
            // Prod Category API
            const catRes = await post(`${base}get_categories`, { domain_id: id, source: "SSP" }, true, false);
            if (catRes?.status === "success") {
                const formattedCats = catRes.data.map(item => ({ label: item, value: item }));
                setCategoryOptions(formattedCats);
            }

            // Prod Location API
            const locRes = await post(`${base}get_locs`, { domain_id: id, source: "SSP" }, true, false);
            if (locRes?.status === "success") {
                const formattedLocs = locRes.data.map(item => ({
                    label: `${item.loc} - ${item.loc_name}`,
                    value: item.loc
                }));
                setLocationOptions(formattedLocs);
            }
        } catch (error) {
            console.error("Error fetching dropdowns", error);
        }
        setLoading(false);
    };

    const downloadAssetQrFile = async () => {
        if (!category) {
            setmessage("Please select Category.");
            setmodalOpener(true);
            return;
        }
        if (quantity === "" || !quantity) {
            setmessage("Please select Quantity");
            setmodalOpener(true);
            return;
        }
        // Dev logic: Location is mandatory if category is NOT "Assets"
        if (category?.value !== "Assets" && !location) {
            setmessage("Please select Location.");
            setmodalOpener(true);
            return;
        }

        let payload = {
            domain_id: id,
            qty: Number(quantity),
            loc: category?.value === "Assets" ? "" : String(location?.value),
            category: category?.value,
            source: "SSP"
        };

        // Prod Download API
        let response = await post(`${base}asset_qr_download`, payload, true, false);

        if (response?.status === "success") {
            window.open(response?.url);
            rowFetch();
            // Reset fields
            setQuantity('');
            setCategory(null);
            setLocation(null);
        } else {
            setmessage(response?.message || "Download failed");
            setmodalOpener(true);
        }
    };

    const downloadFile = async (path) => {
        // Prod Download File API
        let response = await post(
            `${base}asset_qr_dowload_file`,
            {
                file_name: path,
                domain_id: id,
                source: "SSP"
            },
            true,
            false
        );
        if (response?.status === "success") {
            window.open(response?.url);
        } else {
            setmessage(response?.message);
            setmodalOpener(true);
        }
    };

    const customSelectStyles = {
        container: (base) => ({ ...base, width: '100%' }),
        control: (base, state) => ({
            ...base,
            minHeight: "40px",
            borderColor: "black",
            backgroundColor: "white",
            "&:hover": { borderColor: "black" },
            boxShadow: "none",
        }),
        menu: (provided) => ({ ...provided, zIndex: 9999 })
    };

    const columns = [
        { field: "log_id", headerName: "Log ID", flex: 1, headerAlign: 'center', align: 'center' },
        { field: "start_asset_id", headerName: "From Asset ID", flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => (params.row.start_asset_id ? params.row.start_asset_id : 'N/A') },
        { field: "end_asset_id", headerName: "Till Asset ID", flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => (params.row.end_asset_id ? params.row.end_asset_id : 'N/A') },
        { field: "count", headerName: "Count", flex: 1, headerAlign: 'center', align: 'center' },
        { field: "create_date", headerName: "Create Date", flex: 1, headerAlign: 'center', align: 'center' },
        {
            field: "tag_file_path",
            headerName: "File",
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <IconButton aria-label="Download" onClick={() => downloadFile(params.row.tag_file_path)}>
                    <Download color="primary" />
                </IconButton>
            ),
        },
    ];

    return (
        <div style={{ padding: '20px' }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 2,
                p: 2,
                borderRadius: '8px',
                backgroundColor: '#fff',
                maxWidth: '500px',
                mx: 'auto'
            }}>
                <Grid container spacing={2} sx={{ maxWidth: '450px' }}>
                    <Grid item xs={4} display="flex" alignItems="center"><Typography>Category</Typography></Grid>
                    <Grid item xs={8}>
                        <Select
                            options={categoryOptions}
                            value={category}
                            onChange={(selected) => {
                                setCategory(selected);
                                if (selected?.value === "Assets") setLocation(null);
                            }}
                            styles={customSelectStyles}
                            placeholder="Select Category"
                            isSearchable
                        />
                    </Grid>

                    <Grid item xs={4} display="flex" alignItems="center" style={{ marginTop: 7, marginBottom: 7 }}><Typography>Quantity</Typography></Grid>
                    <Grid item xs={8} style={{ marginTop: 7, marginBottom: 7 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={quantity}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || (Number(value) >= 1 && Number(value) <= 150)) {
                                    setQuantity(value);
                                } else {
                                    setmessage('The value should be between 1 and 150.');
                                    setmodalOpener(true);
                                }
                            }}
                            inputProps={{ min: 1, max: 150 }}
                        />
                    </Grid>

                    {/* Conditional Rendering from Dev Code */}
                    {category && category.value !== "Assets" && (
                        <>
                            <Grid item xs={4} display="flex" alignItems="center"><Typography>Location</Typography></Grid>
                            <Grid item xs={8}>
                                <Select
                                    options={locationOptions}
                                    value={location}
                                    onChange={setLocation}
                                    styles={customSelectStyles}
                                    placeholder="Select location code"
                                    isSearchable
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12} display="flex" justifyContent="center" mt={1}>
                        <SSPCommonButton
                            btnText={"Download file"}
                            btnFunction={downloadAssetQrFile}
                        />
                    </Grid>
                </Grid>
            </Box>

            <Card sx={{ backgroundColor: "#EEF2F7", padding: "1.3rem" }}>
                <CardHeader
                    p={2}
                    sx={{
                        backgroundColor: "#F8F8F9",
                        fontWeight: "700",
                        borderTopRightRadius: "5px",
                        borderTopLeftRadius: "5px",
                    }}
                    title={"Previous Downloads"}
                    titleTypographyProps={{
                        sx: { fontSize: "16px", fontWeight: "600", lineHeight: "16px", color: "#0160F1" },
                    }}
                    action={
                        <Button onClick={() => window.location.reload()}>
                            Refresh Page
                        </Button>
                    }
                />
                <Divider />
                <CardContent sx={{ backgroundColor: "#FFFFFF", borderBottomRightRadius: "5px", borderBottomLeftRadius: "5px" }}>
                    <Box sx={{ height: 700, width: "100%" }}>
                        <DataGrid
                            disableColumnMenu={true}
                            rowHeight={80}
                            showCellRightBorder={true}
                            sx={{
                                ".MuiDataGrid-columnHeaderTitle": { fontWeight: "bold !important" },
                                ".MuiDataGrid-cell": { fontSize: "0.8rem" },
                                overflowX: "scroll",
                            }}
                            rows={rows}
                            columns={columns}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 10 } },
                            }}
                            getRowId={(row) => row.id || row.log_id}
                            components={{ Toolbar: GridToolbar }}
                            componentsProps={{
                                Toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>

            {modalOpener && (
                <SSPCommonModal
                    open={modalOpener}
                    handleOpenClose={setmodalOpener}
                    message={message}
                />
            )}
        </div>
    );
};

export default FirstTab;